import React, { useEffect, useMemo, useState } from "react";
import { useParams, useLocation } from "react-router-dom";
import { Helmet } from "react-helmet";
import { useStyles } from "./WordPage.styles";
import QuizResult from "../../components/QuizResult/QuizResult";

const isNonEmptyText = (t) => String(t || "").trim().length > 0;

const fetchWord = async (slug) => {
  const response = await fetch(`/api/word/${encodeURIComponent(slug)}`);
  if (!response.ok) {
    throw new Error("Word not found");
  }
  return response.json();
};

const renderContentBlocks = (content, classes) => {
  if (!Array.isArray(content) || content.length === 0) return null;

  // Build sections: each heading-3 starts a new section; non-heading blocks belong to the current section
  const sections = content.reduce((acc, block) => {
    const curr = acc[acc.length - 1];

    if (block && block.type === "heading-3" && isNonEmptyText(block.text)) {
      acc.push({ heading: block.text, blocks: [] });
      return acc;
    }

    // Only attach blocks if we have a section started
    if (!curr) return acc;

    // Keep only blocks with real content
    if (block && block.type === "paragraph" && isNonEmptyText(block.text)) {
      curr.blocks.push({ type: "paragraph", text: block.text });
    }

    return acc;
  }, []);

  // Render only sections that actually have at least one content block
  const renderable = sections.filter(
    (s) => Array.isArray(s.blocks) && s.blocks.length > 0
  );

  return renderable.map((section, idx) => (
    <div key={`${section.heading}-${idx}`}>
      <h2 className={classes.sectionHeading}>{section.heading}</h2>
      {section.blocks.map((b, j) => {
        if (b.type === "paragraph") {
          return (
            <p key={j} className={classes.paragraph}>
              {b.text}
            </p>
          );
        }
        return null;
      })}
    </div>
  ));
};

const WordPage = () => {
  const classes = useStyles();
  const { slug } = useParams();
  const location = useLocation();
  const [word, setWord] = useState(null);
  const [error, setError] = useState("");

  // Parse query parameters
  const queryParams = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return {
      guess: params.get("guess"),
      pa1: params.get("pa1"),
      pa2: params.get("pa2"),
      pa3: params.get("pa3"),
      pa4: params.get("pa4"),
    };
  }, [location.search]);

  // Build quiz options array if we have quiz params
  const quizOptions = useMemo(() => {
    const { pa1, pa2, pa3, pa4 } = queryParams;
    if (pa1 && pa2 && pa3 && pa4) {
      return [pa1, pa2, pa3, pa4];
    }
    return null;
  }, [queryParams]);

  useEffect(() => {
    let cancelled = false;

    setWord(null);
    setError("");

    fetchWord(slug)
      .then((data) => {
        if (!cancelled) setWord(data);
      })
      .catch(() => {
        if (!cancelled) setError("Word not found");
      });

    return () => {
      cancelled = true;
    };
  }, [slug]);

  const primaryMeaning = useMemo(() => {
    return word && Array.isArray(word.wordMeanings)
      ? word.wordMeanings[0]
      : null;
  }, [word]);

  const contentBlocks = useMemo(() => {
    return word ? renderContentBlocks(word.content, classes) : null;
  }, [word, classes]);

  const pageTitle =
    word && word.title ? `${word.title} | Word Hopper` : "Word | Word Hopper";
  const description =
    (primaryMeaning && primaryMeaning.definition) ||
    "Word definition on Word Hopper.";

  if (error) {
    return (
      <div className={classes.container}>
        <h1>Not Found</h1>
        <p>{error}</p>
      </div>
    );
  }

  if (!word) {
    return (
      <div className={classes.container}>
        <p>Loading…</p>
      </div>
    );
  }

  return (
    <div className={classes.container}>
      <Helmet>
        <title>{pageTitle}</title>
        <link
          rel="canonical"
          href={`https://www.wordhopnow.com/word/${slug}`}
        />
        <meta name="description" content={description} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={description} />
        <meta
          property="og:url"
          content={`https://www.wordhopnow.com/word/${slug}`}
        />
        <meta property="og:type" content="article" />
        {word.image_url ? (
          <meta property="og:image" content={word.image_url} />
        ) : null}
      </Helmet>

      <div className={classes.titleContainer}>
        <h1 className={classes.title}>{word.title}</h1>
        {primaryMeaning && primaryMeaning.partOfSpeech ? (
          <h3 className={classes.partOfSpeech}>
            {primaryMeaning.partOfSpeech}
          </h3>
        ) : null}
      </div>

      <hr className={classes.hr} />

      {queryParams.guess && quizOptions ? (
        <QuizResult
          correctWord={word.title}
          guessedWord={queryParams.guess}
          options={quizOptions}
        />
      ) : null}

      <div className={classes.pronunciation}>
        {word.pronunciation ? (
          <span className={classes.pronunciationText}>
            [{word.pronunciation}]
          </span>
        ) : null}
      </div>

      {primaryMeaning ? (
        <>
          <p className={classes.definition}>{primaryMeaning.definition}</p>

          {Array.isArray(primaryMeaning.synonyms) &&
          primaryMeaning.synonyms.length ? (
            <p className={classes.synonyms}>
              <strong>Synonyms:</strong> {primaryMeaning.synonyms.join(", ")}
            </p>
          ) : null}

          {Array.isArray(primaryMeaning.antonyms) &&
          primaryMeaning.antonyms.length ? (
            <p className={classes.antonyms}>
              <strong>Antonyms:</strong> {primaryMeaning.antonyms.join(", ")}
            </p>
          ) : null}
        </>
      ) : null}

      <hr className={classes.hr} />

      {contentBlocks}
    </div>
  );
};

export default WordPage;
